// i18next-extract-mark-ns-start menu

import React, { useEffect } from "react";
import CategoriesContainer from "src/components/side-menu/CategoriesContainer";
import styled from "styled-components";
import { LazyMotion, domAnimation, m } from "framer-motion";
import tw from "twin.macro";
import {
  useTranslation,
  Link,
} from "gatsby-plugin-react-i18next";
import resolveConfig from "tailwindcss/resolveConfig";
import tailwindConfig from "src/../tailwind.config";
import LabeledSwitch from "../ui/LabeledSwitch";
import LanguageSwitcher from "../language/LanguageSwitcher";
import { 
  clearOpenSelection, 
  setIsAlphabetical, 
  setSortDirection, 
  useIsAlphabetical, 
  useIsMenuOpen, 
  useSearchQuery, 
  useSortDirection,
} from "./side-menu-store";
import { isLargeScreen } from "src/helpers/ScreenSizeContext";
import SearchInput from "./SearchInput";

export interface IMenuItem {
  name: string;
  id: string;
  isActive?: boolean;
  link?: string;
  tag?: string[];
}

const tailwind = resolveConfig(tailwindConfig);

const CategoryFilter = styled.div`
  display: flex;
  margin-left: 15px;
  label {
    margin-top: 3px;
    font-weight: bold;
  }
  div {
    padding-top: 1.5px;
  }
  button {
    padding-top: 2.5px;
  }
  @media (min-width: 13.7rem) {
    height: 40px;
    padding-top: 1px;
  }
  @media (min-width: ${tailwind.theme.screens.lg}) {
    height: 50px;
    padding-top: 2px;
  }
`;

const CategoryContainer = styled.div`
  height: 47px;
  background-color: #90a4b0;
  @media (min-width: 13.7rem) {
    height: 47px;
    padding-top: 8px;
  }
`;

const MenuNav = styled(m.nav)`
  width: 13.7rem;
  overflow: hidden;
  ${tw`fixed`}
  height:50%;
  -webkit-appearance: none;
  -webkit-border-radius: 0;
  border-radius: 0;
  @media (min-width: ${tailwind.theme.screens.lg}) {
    width: 18rem;
    height: 100%;
    -webkit-appearance: none;
    -webkit-border-radius: 0;
    border-radius: 0;
  }
`;

export const SHOW_UPPER_ELEMENT_ON_MOBILE = true;

export interface ISideMenuProps {
  pdfByCategoryPath?: string | null
  pdfAzPath?: string | null
}

const SideMenu = ({
  pdfByCategoryPath,
  pdfAzPath,
}: ISideMenuProps) => {
  const { t } = useTranslation("menu");
  const isMenuOpen = useIsMenuOpen()
  const isAlphabetical = useIsAlphabetical()
  const sortDirection = useSortDirection()
  const searchQuery = useSearchQuery()

  const setDynamicStyles = () => {
    const sideMenu = document.getElementById("side-menu");
    const scrollTop = window.scrollY;
    const header = document.getElementById("header");
    const headerContainer = document.getElementById("header-container");
    const headerHeight = header.offsetHeight;
    const headerOffset = parseInt(headerContainer.style.top, 10) || 0;
    const offsetTop = Math.max(headerHeight + headerOffset, 0);

    const bodyHeight = document.querySelector("body").clientHeight;
    const windowHeight = window.innerHeight;
    const scrollableBottomSpace = bodyHeight - (scrollTop + windowHeight);

    const stickyFooterHeight = document.getElementById("sticky-footer")
      .offsetHeight;
    const footerHeight = document.getElementById("footer").offsetHeight;

    const largeScreenBottomOffset = Math.max(
      footerHeight + stickyFooterHeight - scrollableBottomSpace,
      stickyFooterHeight
    );

    const getBottomPosition = () => {
      if (!isLargeScreen()) {
        return "0";
      }
      return largeScreenBottomOffset;
    };

    sideMenu.style.bottom = `${getBottomPosition()}px`;
    sideMenu.style.top = `${offsetTop}px`;
    sideMenu.style.height = `auto`;
  };

  const navVariants = () => ({
    open: {
      opacity: 1,
      display: "block",
      x: 0,
    },
    closed: {
      opacity: 0,
      x: isLargeScreen() ? "-100%" : "100%",
      transitionEnd: {
        display: "none",
      },
    },
  });

  const updateLayoutDynamicValues = () => {
    /**
     * setTimeout is used to delay the execution of this method.
     * It needs to be fired after updating the position of the header in Header.tsx -> showHeaderWhenScrollingUp
     * I believe it's a temporary solution
     * TODO: remove setTimeout and prepare some more meaningfull solution.
     */
    setTimeout(() => {
      setDynamicStyles();
    });
  };

  useEffect(() => {
    updateLayoutDynamicValues();
    window.addEventListener("resize", updateLayoutDynamicValues);
    window.addEventListener("scroll", updateLayoutDynamicValues);
    return () => {
      window.removeEventListener("resize", updateLayoutDynamicValues);
      window.removeEventListener("scroll", updateLayoutDynamicValues);
    };
  }, []);

  return (
    <LazyMotion features={domAnimation}>
      <MenuNav
        id='side-menu'
        animate={isMenuOpen ? "open" : "closed"}
        initial={isMenuOpen ? navVariants().open : navVariants().closed}
        variants={navVariants()}
        transition={{ type: "Inertia", stiffness: 50 }}
        style={{
          bottom: "245px",
          top: "57px",
        }}
        className={'absolute flex-col items-between content-between lg:fixed right-0 bg-almostBlackBlue ' +
          'lg:right-auto lg:left-0 shadow-2xl z-20 border-grayBlue border-b h-auto'
        }
      >
        <div
          className="flex lg:block h-full flex-col overflow-x-hidden overflow-y-auto">
          <SearchInput />

          {!searchQuery && (
            <CategoryContainer>
              <CategoryFilter>
                <label>{t('CATEGORIES')}</label>{" "}
                <div id="categories-switch-container">
                  <LabeledSwitch
                    value={isAlphabetical}
                    onChange={(newValue => {
                      setIsAlphabetical(newValue)
                      if (!newValue && !searchQuery) {
                        clearOpenSelection()
                      }
                    })}
                    rightLabel={sortDirection === 'asc' ? t('A-Z') : t('Z-A')}
                    onRightLabelClick={
                      isAlphabetical 
                        ? () => {
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
                        } 
                        : undefined
                      }
                  />
                </div>
              </CategoryFilter>
            </CategoryContainer>
          )}
          <div className="flex flex-col h-full lg:h-auto justify-between">
            <div>
              <CategoriesContainer pdfByCategoryPath={pdfByCategoryPath} pdfAzPath={pdfAzPath} />
            </div>
            <div className='b-0 l-0 r-0 absolute h-1 bg-navyBlueGray' />
            <div className="lg:hidden text-appGreen uppercase text-sm">
              <div className='py-3.5 px-4 border-t border-appGreen'>
                <LanguageSwitcher />
              </div>
              <div className='py-3.5 px-4 border-t border-appGreen'>
                <Link to="/footballrulesapp/">{t('Download mobile app')}</Link>
              </div>
              <div className='py-3.5 px-4 border-t border-appGreen'>
                <a href="https://www.theifab.com/">THEIFAB.COM</a>
              </div>
            </div>
          </div>
        </div>
      </MenuNav>
    </LazyMotion>
  )
}

export default SideMenu;
